import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, Select, Switch, PanelCard, Chip } from '@glplus/glpweb-dsl';
import { DAY_KEYS, TIME_OPTIONS, type WeekHours, type DayKey } from '../../lib/hours';
import type { FnsVerification } from '../../lib/vendorApiClient';

/**
 * The three cards every location screen shares (Figma L2 9:44, L3 11:129,
 * L5 12:359): Location details · Hours · Services, plus the SNAP / EBT card.
 * Pure controlled UI; the routes own the state and the API calls.
 */

export interface LocationFormState {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  website: string;
  hours: WeekHours;
  pickupSupported: boolean;
  temporarilyClosed: boolean;
  fnsNumber: string;
}

const HEADING = '#303A42';
const MUTED = '#5A7063';
const CARD_SX = { height: 'auto', bgcolor: '#fff' } as const;

export function LocationDetailsCard({ form, onChange, disabled }: { form: LocationFormState; onChange: (patch: Partial<LocationFormState>) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const f = (label: string, key: keyof LocationFormState, opts: { placeholder?: string; type?: string; maxLength?: number } = {}) => (
    <TextField
      label={label}
      ariaLabel={label}
      type={opts.type ?? 'text'}
      placeholder={opts.placeholder}
      value={String(form[key] ?? '')}
      onChange={(e) => onChange({ [key]: e.target.value } as Partial<LocationFormState>)}
      disabled={disabled}
      fullWidth
      slotProps={{ htmlInput: { maxLength: opts.maxLength ?? 200 } }}
    />
  );
  return (
    <PanelCard title={t('locations.form.detailsTitle')} subtitle={t('locations.form.detailsSubtitle')} sx={CARD_SX}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {f(t('locations.form.name'), 'name', { placeholder: t('locations.form.namePlaceholder'), maxLength: 120 })}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 1fr' }, gap: 2 }}>
          {f(t('locations.form.line1'), 'line1', { placeholder: t('locations.form.line1Placeholder') })}
          {f(t('locations.form.line2'), 'line2', { placeholder: t('locations.form.optional') })}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2 }}>
          {f(t('locations.form.city'), 'city', { maxLength: 100 })}
          {f(t('locations.form.state'), 'state', { maxLength: 2 })}
          {f(t('locations.form.zip'), 'postalCode', { maxLength: 10 })}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {f(t('locations.form.phone'), 'phone', { type: 'tel', maxLength: 40 })}
          {f(t('locations.form.website'), 'website', { type: 'url', placeholder: t('locations.form.optional') })}
        </Box>
      </Box>
    </PanelCard>
  );
}

export function HoursCard({ hours, onChange, disabled }: { hours: WeekHours; onChange: (h: WeekHours) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const setDay = (day: DayKey, patch: Partial<WeekHours[DayKey]>) => onChange({ ...hours, [day]: { ...hours[day], ...patch } });
  return (
    <PanelCard title={t('locations.form.hoursTitle')} subtitle={t('locations.form.hoursSubtitle')} sx={CARD_SX}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {DAY_KEYS.map((day) => {
          const d = hours[day];
          const open = !d.closed;
          return (
            <Box key={day} sx={{ display: 'grid', gridTemplateColumns: '110px 64px 1fr 24px 1fr', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: HEADING }}>{t(`locations.days.${day}`)}</Typography>
              <Switch ariaLabel={t('locations.form.openOn', { day: t(`locations.days.${day}`) })} checked={open} disabled={disabled} color="#3DA026" onChange={(_e, on) => setDay(day, on ? { closed: false, open: d.open ?? '0800', close: d.close ?? '2000' } : { closed: true })} />
              {open ? (
                <>
                  <Select ariaLabel={t('locations.form.opens', { day: t(`locations.days.${day}`) })} value={d.open ?? '0800'} options={TIME_OPTIONS} size="small" disabled={disabled} onChange={(_e, v) => setDay(day, { open: v })} />
                  <Typography sx={{ textAlign: 'center', color: MUTED, fontSize: 13 }}>{t('locations.form.to')}</Typography>
                  <Select ariaLabel={t('locations.form.closes', { day: t(`locations.days.${day}`) })} value={d.close ?? '2000'} options={TIME_OPTIONS} size="small" disabled={disabled} onChange={(_e, v) => setDay(day, { close: v })} />
                </>
              ) : (
                <Typography sx={{ gridColumn: 'span 3', color: MUTED, fontSize: 14 }}>{t('locations.form.closed')}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </PanelCard>
  );
}

export function ServicesCard({ form, onChange, disabled, showTemporarilyClosed = true }: { form: LocationFormState; onChange: (patch: Partial<LocationFormState>) => void; disabled?: boolean; showTemporarilyClosed?: boolean }) {
  const { t } = useTranslation();
  const row = (key: 'pickupSupported' | 'temporarilyClosed', labelKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.25 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: HEADING }}>{t(`locations.services.${labelKey}`)}</Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>{t(`locations.services.${labelKey}Help`)}</Typography>
      </Box>
      <Switch ariaLabel={t(`locations.services.${labelKey}`)} checked={form[key]} disabled={disabled} color="#3DA026" onChange={(_e, on) => onChange({ [key]: on } as Partial<LocationFormState>)} />
    </Box>
  );
  return (
    <PanelCard title={t('locations.form.servicesTitle')} subtitle={t('locations.form.servicesSubtitle')} sx={CARD_SX}>
      {row('pickupSupported', 'pickup')}
      <Box sx={{ py: 1.25 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: HEADING }}>{t('locations.services.deliveryCoverage')}</Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>{t('locations.services.deliveryCoverageHelp')}</Typography>
      </Box>
      {showTemporarilyClosed && row('temporarilyClosed', 'temporarilyClosed')}
    </PanelCard>
  );
}

export function SnapCard({ form, onChange, verification, verifiedAt, disabled }: { form: LocationFormState; onChange: (patch: Partial<LocationFormState>) => void; verification: FnsVerification | null; verifiedAt?: string | null; disabled?: boolean }) {
  const { t } = useTranslation();
  const label = t('locations.snap.fnsLabel');
  return (
    <PanelCard title={t('locations.snap.title')} subtitle={t('locations.snap.subtitle')} sx={CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '0 1 260px' }}>
          <TextField label={label} ariaLabel={label} placeholder={t('locations.snap.placeholder')} value={form.fnsNumber} disabled={disabled} onChange={(e) => onChange({ fnsNumber: e.target.value.replace(/\D/g, '').slice(0, 7) })} fullWidth slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 7 } }} />
        </Box>
        {verification === 'verified' && (
          <Box sx={{ pb: 1 }}>
            <Chip label={`● ${t('locations.snap.verified')}`} size="small" backgroundColor="#EAF5EA" textColor="#1F7026" fontSize={12} />
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>{t('locations.snap.verifiedWith', { date: verifiedAt ? new Date(verifiedAt).toLocaleDateString() : '' })}</Typography>
          </Box>
        )}
        {verification === 'unverified' && form.fnsNumber && (
          <Chip label={`● ${t('locations.snap.checking')}`} size="small" backgroundColor="#FEF1E7" textColor="#A85200" fontSize={12} sx={{ mb: 1 }} />
        )}
        {verification === 'failed' && (
          <Chip label={`● ${t('locations.snap.failed')}`} size="small" backgroundColor="#fff" textColor="#C62828" fontSize={12} sx={{ mb: 1, border: '1px solid #C62828' }} />
        )}
      </Box>
    </PanelCard>
  );
}

/** "How shoppers see this location" — the L2 / L5 preview card. */
export function ShopperPreviewCard({ name, businessType, hoursText, snap, pickup, live }: { name: string; businessType: string; hoursText: string; snap: boolean; pickup: boolean; live: boolean }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED, mb: 1 }}>
        {(live ? t('locations.preview.title') : t('locations.preview.titleFuture')).toUpperCase()}
      </Typography>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E5E5', borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ height: 110, bgcolor: '#E5E5E5' }} />
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: HEADING }}>{name || '—'}</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            {businessType}
            {' · '}
            {live ? hoursText || t('locations.preview.noHours') : t('locations.preview.notVisible')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {snap && <Chip label="SNAP/EBT" size="small" backgroundColor="#EAF5EA" textColor="#1F7026" fontSize={11} />}
            {pickup && <Chip label={t('locations.services.pickup')} size="small" backgroundColor="#EBF4FA" textColor="#1D2740" fontSize={11} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
